/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { OfferApprovalRequest } from '../models/offer-approval-request';
import { OfferApprovalResponse } from '../models/offer-approval-response';
import { OfferCalculationStatusResponse } from '../models/offer-calculation-status-response';
import { OfferDetailedResponse } from '../models/offer-detailed-response';
import { OfferModificationRequest } from '../models/offer-modification-request';
import { OfferRequestCreateResponse } from '../models/offer-request-create-response';
import { OfferRequestOffersResponse } from '../models/offer-request-offers-response';

/**
 * Offer resource endpoints.
 */
@Injectable({
  providedIn: 'root',
})
export class OffersService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /**
   * Path part for operation getOfferDetailsByOfferRequestIdAndOfferId
   */
  static readonly GetOfferDetailsByOfferRequestIdAndOfferIdPath =
    '/offers/requests/{offerRequestId}/offers/{offerId}';

  /**
   * Get the specified offer details.
   *
   * Get the specified offer details with its UUID and offer request with the given UUID
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getOfferDetailsByOfferRequestIdAndOfferId()` instead.
   *
   * This method doesn't expect any request body.
   */
  getOfferDetailsByOfferRequestIdAndOfferId$Response(
    params: {
      offerRequestId: string;
      offerId: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<OfferDetailedResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      OffersService.GetOfferDetailsByOfferRequestIdAndOfferIdPath,
      'get'
    );
    if (params) {
      rb.path('offerRequestId', params.offerRequestId, {});
      rb.path('offerId', params.offerId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<OfferDetailedResponse>;
        })
      );
  }

  /**
   * Get the specified offer details.
   *
   * Get the specified offer details with its UUID and offer request with the given UUID
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getOfferDetailsByOfferRequestIdAndOfferId$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getOfferDetailsByOfferRequestIdAndOfferId(
    params: {
      offerRequestId: string;
      offerId: string;
    },
    context?: HttpContext
  ): Observable<OfferDetailedResponse> {
    return this.getOfferDetailsByOfferRequestIdAndOfferId$Response(
      params,
      context
    ).pipe(
      map(
        (r: StrictHttpResponse<OfferDetailedResponse>) =>
          r.body as OfferDetailedResponse
      )
    );
  }

  /**
   * Path part for operation exportExcelOfferDetailsByOfferRequestIdAndOfferId
   */
  static readonly GetOfferExcelExportByOfferRequestIdAndOfferIdPath =
    '/offers/requests/{offerRequestId}/offers/{offerId}/excel';

  /**
   * Export the specified offer in Excel format.
   *
   * Get the specified offer in Excel format with its UUID and offer request with the given UUID
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `exportOfferDetailsByOfferRequestIdAndOfferId()` instead.
   *
   * This method doesn't expect any request body.
   */
  exportOfferDetailsByOfferRequestIdAndOfferId$Response(
    params: {
      offerRequestId: string;
      offerId: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<any>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      OffersService.GetOfferExcelExportByOfferRequestIdAndOfferIdPath,
      'get'
    );
    if (params) {
      rb.path('offerRequestId', params.offerRequestId, {});
      rb.path('offerId', params.offerId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'blob',
          accept:
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<any>;
        })
      );
  }

  /**
   * Export the specified offer.
   *
   * Export the specified offer details with its UUID and offer request with the given UUID
   **/
  exportOfferDetailsByOfferRequestIdAndOfferId(
    params: {
      offerRequestId: string;
      offerId: string;
    },
    context?: HttpContext
  ) {
    this.exportOfferDetailsByOfferRequestIdAndOfferId$Response(
      params,
      context
    ).subscribe((r: HttpResponse<any>) => {
      let cdph = r.headers.get('Content-Disposition');
      const contentDispositionHeader: string = cdph
        ? cdph
        : 'offer.' + params.offerId + '.xlsx';
      const fileName = contentDispositionHeader
        .split(';')
        .filter((x) => x.startsWith('filename'))
        .map((x) => x.trim().split('=')[1].trim())
        .join('');

      const link = document.createElement('a');
      link.download = fileName;
      link.href = window.URL.createObjectURL(r.body);
      link.click();
      window.URL.revokeObjectURL(link.href);
    });
  }

  /**
   * Path part for operation updateOffer
   */
  static readonly UpdateOfferPath =
    '/offers/requests/{offerRequestId}/offers/{offerId}';

  /**
   * Changes the quantity of the items in the specified offer.
   *
   * Changing the quantity can result in quantity and basket discount changes as well.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateOffer()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateOffer$Response(
    params: {
      offerRequestId: string;
      offerId: string;
      body: OfferModificationRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<OfferDetailedResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      OffersService.UpdateOfferPath,
      'put'
    );
    if (params) {
      rb.path('offerRequestId', params.offerRequestId, {});
      rb.path('offerId', params.offerId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<OfferDetailedResponse>;
        })
      );
  }

  /**
   * Changes the quantity of the items in the specified offer.
   *
   * Changing the quantity can result in quantity and basket discount changes as well.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateOffer$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateOffer(
    params: {
      offerRequestId: string;
      offerId: string;
      body: OfferModificationRequest;
    },
    context?: HttpContext
  ): Observable<OfferDetailedResponse> {
    return this.updateOffer$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<OfferDetailedResponse>) =>
          r.body as OfferDetailedResponse
      )
    );
  }

  /**
   * Path part for operation createOfferRequest
   */
  static readonly CreateOfferRequestPath = '/offers/requests';

  /**
   * Creates a new offer request.
   *
   * Creates an offer request containing the given parameters.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createOfferRequest()` instead.
   *
   * This method doesn't expect any request body.
   */
  createOfferRequest$Response(
    params?: {},
    context?: HttpContext
  ): Observable<StrictHttpResponse<OfferRequestCreateResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      OffersService.CreateOfferRequestPath,
      'post'
    );
    if (params) {
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<OfferRequestCreateResponse>;
        })
      );
  }

  /**
   * Creates a new offer request.
   *
   * Creates an offer request containing the given parameters.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createOfferRequest$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  createOfferRequest(
    params?: {},
    context?: HttpContext
  ): Observable<OfferRequestCreateResponse> {
    return this.createOfferRequest$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<OfferRequestCreateResponse>) =>
          r.body as OfferRequestCreateResponse
      )
    );
  }

  /**
   * Path part for operation approveOffer
   */
  static readonly ApproveOfferPath =
    '/offers/requests/{offerRequestId}/offers/{offerId}/approve';

  /**
   * Approve the specified offer.
   *
   * Approve the specified offer with its UUID and offer request with the given UUID
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `approveOffer()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  approveOffer$Response(
    params: {
      offerRequestId: string;
      offerId: string;
      body: OfferApprovalRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<OfferApprovalResponse>>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      OffersService.ApproveOfferPath,
      'post'
    );
    if (params) {
      rb.path('offerRequestId', params.offerRequestId, {});
      rb.path('offerId', params.offerId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<Array<OfferApprovalResponse>>;
        })
      );
  }

  /**
   * Approve the specified offer.
   *
   * Approve the specified offer with its UUID and offer request with the given UUID
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `approveOffer$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  approveOffer(
    params: {
      offerRequestId: string;
      offerId: string;
      body: OfferApprovalRequest;
    },
    context?: HttpContext
  ): Observable<Array<OfferApprovalResponse>> {
    return this.approveOffer$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<Array<OfferApprovalResponse>>) =>
          r.body as Array<OfferApprovalResponse>
      )
    );
  }

  /**
   * Path part for operation getOfferRequestById
   */
  static readonly GetOfferRequestByIdPath = '/offers/requests/{offerRequestId}';

  /**
   * Get a specific offer request.
   *
   * Get a specific offer request with the given UUID
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getOfferRequestById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getOfferRequestById$Response(
    params: {
      offerRequestId: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<OfferRequestCreateResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      OffersService.GetOfferRequestByIdPath,
      'get'
    );
    if (params) {
      rb.path('offerRequestId', params.offerRequestId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<OfferRequestCreateResponse>;
        })
      );
  }

  /**
   * Get a specific offer request.
   *
   * Get a specific offer request with the given UUID
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getOfferRequestById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getOfferRequestById(
    params: {
      offerRequestId: string;
    },
    context?: HttpContext
  ): Observable<OfferRequestCreateResponse> {
    return this.getOfferRequestById$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<OfferRequestCreateResponse>) =>
          r.body as OfferRequestCreateResponse
      )
    );
  }

  /**
   * Path part for operation getOfferCalculationStatusByOfferRequestId
   */
  static readonly GetOfferCalculationStatusByOfferRequestIdPath =
    '/offers/requests/{offerRequestId}/status';

  /**
   * Get status from a specified offer request.
   *
   * Get status from a specified offer request with the given UUID
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getOfferCalculationStatusByOfferRequestId()` instead.
   *
   * This method doesn't expect any request body.
   */
  getOfferCalculationStatusByOfferRequestId$Response(
    params: {
      offerRequestId: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<OfferCalculationStatusResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      OffersService.GetOfferCalculationStatusByOfferRequestIdPath,
      'get'
    );
    if (params) {
      rb.path('offerRequestId', params.offerRequestId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<OfferCalculationStatusResponse>;
        })
      );
  }

  /**
   * Get status from a specified offer request.
   *
   * Get status from a specified offer request with the given UUID
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getOfferCalculationStatusByOfferRequestId$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getOfferCalculationStatusByOfferRequestId(
    params: {
      offerRequestId: string;
    },
    context?: HttpContext
  ): Observable<OfferCalculationStatusResponse> {
    return this.getOfferCalculationStatusByOfferRequestId$Response(
      params,
      context
    ).pipe(
      map(
        (r: StrictHttpResponse<OfferCalculationStatusResponse>) =>
          r.body as OfferCalculationStatusResponse
      )
    );
  }

  /**
   * Path part for operation getOffersByOfferRequestId
   */
  static readonly GetOffersByOfferRequestIdPath =
    '/offers/requests/{offerRequestId}/offers';

  /**
   * Get all offers from a specified offer request.
   *
   * Get all offers from a specified offer request with the given UUID
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getOffersByOfferRequestId()` instead.
   *
   * This method doesn't expect any request body.
   */
  getOffersByOfferRequestId$Response(
    params: {
      offerRequestId: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<OfferRequestOffersResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      OffersService.GetOffersByOfferRequestIdPath,
      'get'
    );
    if (params) {
      rb.path('offerRequestId', params.offerRequestId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<OfferRequestOffersResponse>;
        })
      );
  }

  /**
   * Get all offers from a specified offer request.
   *
   * Get all offers from a specified offer request with the given UUID
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getOffersByOfferRequestId$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getOffersByOfferRequestId(
    params: {
      offerRequestId: string;
    },
    context?: HttpContext
  ): Observable<OfferRequestOffersResponse> {
    return this.getOffersByOfferRequestId$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<OfferRequestOffersResponse>) =>
          r.body as OfferRequestOffersResponse
      )
    );
  }

  /**
   * Path part for operation getOfferDetailsByOfferRequestIdAndOfferIdAsExcel
   */
  static readonly GetOfferDetailsByOfferRequestIdAndOfferIdAsExcelPath =
    '/offers/requests/{offerRequestId}/offers/{offerId}/excel';

  /**
   * Get the specified offer details in Excel.
   *
   * Get the specified offer details in Excel format
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getOfferDetailsByOfferRequestIdAndOfferIdAsExcel()` instead.
   *
   * This method doesn't expect any request body.
   */
  getOfferDetailsByOfferRequestIdAndOfferIdAsExcel$Response(
    params: {
      offerRequestId: string;
      offerId: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<string>>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      OffersService.GetOfferDetailsByOfferRequestIdAndOfferIdAsExcelPath,
      'get'
    );
    if (params) {
      rb.path('offerRequestId', params.offerRequestId, {});
      rb.path('offerId', params.offerId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'blob',
          accept:
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<Array<string>>;
        })
      );
  }

  /**
   * Get the specified offer details in Excel.
   *
   * Get the specified offer details in Excel format
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getOfferDetailsByOfferRequestIdAndOfferIdAsExcel$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getOfferDetailsByOfferRequestIdAndOfferIdAsExcel(
    params: {
      offerRequestId: string;
      offerId: string;
    },
    context?: HttpContext
  ): Observable<Array<string>> {
    return this.getOfferDetailsByOfferRequestIdAndOfferIdAsExcel$Response(
      params,
      context
    ).pipe(
      map((r: StrictHttpResponse<Array<string>>) => r.body as Array<string>)
    );
  }
}
